import { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, UserCheck, UserPlus, Share2, Flag, Ban, Bookmark, MapPin } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useFollow } from '@/hooks/useToggleService';
import { useAuth } from '@/lib/auth';

interface PostHeaderProps {
  userId: string;
  username: string;
  avatarUrl: string;
  verified: boolean;
  createdAt: string;
  location?: string;
  onOptionsClick?: (e: MouseEvent) => void;
  isPagePost?: boolean;
}

/**
 * Post header component showing user info, follow button, and post options
 */
export default function PostHeader({
  userId,
  username,
  avatarUrl,
  verified,
  createdAt: _createdAt,
  location,
  onOptionsClick,
  isPagePost = false
}: PostHeaderProps) {
  const { isFollowing, toggleFollow } = useFollow(userId);
  const { user } = useAuth();

  return (
    <div className="flex items-center justify-between w-full p-3">
      <div className="flex items-center gap-2">
        <Link to={`/profile/${username}`} className="flex-shrink-0" onClick={e => e.stopPropagation()}>
          <Avatar className={`h-9 w-9 border border-gray-200 dark:border-gray-700 ${isPagePost ? 'rounded-full' : 'rounded-lg'}`}>
            <AvatarImage src={avatarUrl} alt={username} />
            <AvatarFallback>{username.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </Link>

        <div className="flex flex-col">
          <Link
            to={`/profile/${username}`}
            className="flex items-center gap-1 font-semibold text-sm hover:underline"
            onClick={e => e.stopPropagation()}
          >
            {username}
            {verified && (
              <svg className="w-3.5 h-3.5 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
            )}
          </Link>
          {/* Display location if it exists */}
          {location && (
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <MapPin className="h-3 w-3" />
              <span>{location}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 ml-auto">
        {user?.id !== userId && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFollow();
            }}
            className="p-2 rounded-md hover:bg-[#065900] active:bg-[#054700] transition-colors duration-200 group"
          >
            {isFollowing ? (
              <UserCheck className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-white group-active:text-white" />
            ) : (
              <UserPlus className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-white group-active:text-white" />
            )}
          </button>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-md hover:bg-[#065900] active:bg-[#054700] transition-colors duration-200 group"
            >
              <MoreHorizontal className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-white group-active:text-white" />
            </button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-48 p-0 z-50" 
            align="end" 
            side="bottom" 
            sideOffset={5}
            avoidCollisions={true}
            collisionPadding={10}
          >
            <div className="py-1">
              <button 
                className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200 group"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOptionsClick) onOptionsClick(e);
                }}
              >
                <Share2 className="h-4 w-4 group-hover:text-white" />
                <span>Share</span>
              </button>
              <button className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200 group">
                <Bookmark className="h-4 w-4 group-hover:text-white" />
                <span>Save</span>
              </button>
              <button className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white transition-colors duration-200 group">
                <Flag className="h-4 w-4 group-hover:text-white" />
                <span>Report</span>
              </button>
              <button className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-[#065900] active:bg-[#054700] transition-colors duration-200 text-red-500 hover:text-white group">
                <Ban className="h-4 w-4 group-hover:text-white" />
                <span>Not interested</span>
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
